import React from "react";
import { Link } from "react-router-dom";
import "./Home.css";

const activities = [
  "Walking",
  "Running",
  "Cycling",
  "Hiking",
  "Dog walking",
  "Commuting",
  "School run",
  "Gardening",
  "Parkrun",
  "Hanging washing",
];

const benefits = [
  {
    title: "Understand",
    copy: "See the main issue and how conditions could affect you and your activity.",
  },
  {
    title: "Prepare",
    copy: "Get practical advice on kit, surfaces and what to watch for.",
  },
  {
    title: "Adapt",
    copy: "Understand when your route, timing or approach may need changing.",
  },
];

const reportSections = [
  "Overall Result",
  "Human Impact",
  "Activity Impact",
  "Recommended Kit",
  "Activity Strategy",
  "Preparation Summary",
  "AERS & Confidence",
  "Safety Alerts when relevant",
];

function Home() {
  return (
    <div className="home-page">
      <section className="home-hero" aria-labelledby="home-title">
        <div className="home-hero-copy">
          <p className="home-eyebrow">ASDRY BETA</p>
          <h1 className="home-title" id="home-title">
            Know what the weather means for your plans.
          </h1>
          <p className="home-hero-lead">
            ASDRY turns weather and recent conditions into practical advice for
            the activity you&apos;re planning.
          </p>
          <p className="home-hero-activities">
            Walking, running, cycling, commuting, dog walks and more.
          </p>
          <div className="home-hero-actions">
            <a className="home-button home-button-primary" href="https://asdry.app/">
              Try ASDRY Free →
            </a>
            <a className="home-text-link" href="#how-it-works">
              See how it works ↓
            </a>
          </div>
        </div>

        <aside className="home-result-panel" aria-label="Example ASDRY result summary">
          <div className="home-result-panel-header">
            <span>Example result</span>
            <span className="home-result-activity">Walking</span>
          </div>
          <p className="home-result-label">Overall Result</p>
          <p className="home-result-status">HIGH CAUTION</p>
          <p className="home-result-copy">
            Recent rain may leave some paths less reliable underfoot.
          </p>
          <div className="home-result-points">
            <p><strong>Prepare</strong> Choose suitable footwear.</p>
            <p><strong>Adapt</strong> Prefer firmer paths where possible.</p>
          </div>
        </aside>
      </section>

      <section
        className="home-section home-comparison"
        id="how-it-works"
        aria-labelledby="home-comparison-title"
      >
        <div className="home-section-heading">
          <p className="home-eyebrow">FORECAST → ASDRY</p>
          <h2 className="home-section-title" id="home-comparison-title">
            The forecast tells you the weather. ASDRY helps explain what it means.
          </h2>
        </div>

        <div className="home-comparison-grid">
          <article className="home-comparison-card">
            <div className="home-card-heading-row">
              <h3 className="home-card-title">Forecast</h3>
              <span className="home-example-label">Example</span>
            </div>
            <dl className="home-forecast-list">
              <div><dt>Temperature</dt><dd>13°C</dd></div>
              <div><dt>Wind</dt><dd>Light wind</dd></div>
              <div><dt>Rain</dt><dd>No rain now</dd></div>
              <div><dt>Recent rain</dt><dd>1.3 mm</dd></div>
            </dl>
          </article>

          <article className="home-comparison-card home-comparison-card-asdry">
            <div className="home-card-heading-row">
              <h3 className="home-card-title">ASDRY</h3>
              <span className="home-example-label">Example</span>
            </div>
            <p className="home-impact-label">Ground &amp; surface conditions</p>
            <p className="home-impact-status">HIGH CAUTION</p>
            <p className="home-impact-copy">
              Recent rain may leave paths and crossings less reliable underfoot.
            </p>
            <div className="home-impact-advice">
              <p><strong>Prepare</strong> Footwear suitable for damp or uneven ground.</p>
              <p><strong>Adapt</strong> Use firmer paths where puddles or mud collect.</p>
            </div>
          </article>
        </div>

        <p className="home-comparison-summary">
          The forecast gives you the conditions. ASDRY helps you understand what
          they may mean for your activity.
        </p>
      </section>

      <section className="home-section home-benefits" aria-labelledby="home-benefits-title">
        <div className="home-section-heading">
          <p className="home-eyebrow">PRACTICAL GUIDANCE</p>
          <h2 className="home-section-title" id="home-benefits-title">
            From conditions to something useful.
          </h2>
        </div>
        <div className="home-benefit-grid">
          {benefits.map((benefit) => (
            <article className="home-benefit-card" key={benefit.title}>
              <span className="home-benefit-mark" aria-hidden="true">✓</span>
              <h3 className="home-benefit-title">{benefit.title}</h3>
              <p className="home-benefit-copy">{benefit.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section home-activities" aria-labelledby="home-activities-title">
        <div className="home-section-heading">
          <p className="home-eyebrow">EVERYDAY ACTIVITIES</p>
          <h2 className="home-section-title" id="home-activities-title">
            Built around what you&apos;re actually doing.
          </h2>
          <p className="home-section-copy">
            Choose your activity, location and time. ASDRY does the rest.
          </p>
        </div>
        <ul className="home-activity-list" aria-label="Activities supported by ASDRY">
          {activities.map((activity) => (
            <li className="home-activity-pill" key={activity}>{activity}</li>
          ))}
        </ul>
      </section>

      <section className="home-section home-report-preview" aria-labelledby="home-report-title">
        <div className="home-report-intro">
          <p className="home-eyebrow">ASDRY FREE</p>
          <h2 className="home-section-title" id="home-report-title">
            Start with a useful Free report.
          </h2>
          <p className="home-section-copy">
            ASDRY Free gives you a practical view of how the conditions may affect
            your plans before you set off.
          </p>
          <ul className="home-report-sections" aria-label="Sections available in an ASDRY Free report">
            {reportSections.map((section) => (
              <li key={section}><span aria-hidden="true">✓</span>{section}</li>
            ))}
          </ul>
          <a className="home-button home-button-primary" href="https://asdry.app/">
            Create your Free report →
          </a>
        </div>

        <article className="home-report-card" aria-label="Example ASDRY Free report result">
          <div className="home-report-card-header">
            <div>
              <p className="home-report-kicker">Example Free report</p>
              <h3 className="home-report-card-title">Overall Result</h3>
            </div>
            <span className="home-report-activity">Commute</span>
          </div>
          <div className="home-report-result">
            <p className="home-report-status">HIGH CAUTION</p>
            <p>
              Rain may leave paths and crossings less reliable underfoot during
              the outdoor part of the commute.
            </p>
          </div>
          <div className="home-report-snippets">
            <div><span>Human Impact</span><strong>Take extra care on damp surfaces.</strong></div>
            <div><span>Recommended Kit</span><strong>Wear footwear with reliable grip.</strong></div>
            <div><span>Activity Strategy</span><strong>Allow time for firmer crossings.</strong></div>
          </div>
        </article>
      </section>

      <section className="home-section home-field-tester" aria-labelledby="home-field-tester-title">
        <div>
          <p className="home-eyebrow">FIELD TESTER PRO</p>
          <h2 className="home-section-title" id="home-field-tester-title">
            Want to help improve ASDRY?
          </h2>
          <p className="home-field-tester-subtitle">Become an ASDRY Field Tester.</p>
          <p className="home-section-copy">
            Test ASDRY in real conditions, tell us how well the report matched what
            you experienced, and get access to Field Tester Pro while you participate.
          </p>
          <p className="home-field-tester-note">Places are limited and applications are reviewed.</p>
        </div>
        <div className="home-field-tester-action">
          <Link className="home-pending-link" to="/field-tester-pro">
            Learn about Field Tester Pro →
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
